/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { IRequestContext } from '@msdyn365-commerce/core';
import {
    AccountStatementRequest, BusinessPartnerOperationDeliveryType,
    BusinessPartnerUser, BusinessPartnerUsersDataActions
} from '@msdyn365-commerce/retail-proxy';
import { AdminRequestStatementButton, RequestStatementModal } from '@msdyn365-commerce-modules/account-management';
import {
    addOrganizationUser, AddOrganizationUserInput, deleteOrganizationUser,
    DeleteOrganizationUserInput, editOrganizationUser, EditOrganizationUserInput,
    EmailRegex, Random, StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, getPayloadObject, getTelemetryAttributes, getTelemetryObject, Heading, IDataTableProps, IHeadingsProperty, IModuleProps, INodeProps, IPayLoad,
    ITableItemProps, ITableRowProps, ITelemetryContent, Table, TableDataType, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IBusinessOrganizationListData } from './business-organization-list.data';
import { IBusinessOrganizationListProps, IBusinessOrganizationListResources } from './business-organization-list.props.autogenerated';
import { FormButton, FormFieldError, FormInput, FormLabel, IBusinessFormItem, IUserFormItem } from './components';

export interface IBusinessOrganizationListViewProps extends IBusinessOrganizationListProps<IBusinessOrganizationListData> {
    OrganizationUserList: IModuleProps;
    moduleContainer: INodeProps;
    heading: React.ReactNode;
    noUsersText?: string;
    addUserButton?: React.ReactNode;
    search?: React.ReactNode;
    organizationListTable: React.ReactNode;
    form?: IFormViewProps;
    formItems?: IUserFormItem[];
    formType: FormType;
    deleteUserForm?: React.ReactNode;
    viewUserDetails?: React.ReactNode;
    modalHeading?: React.ReactNode;
    modalState?: boolean;
    requestAccountStatementButton: React.ReactNode;
    requestStatementModal: React.ReactNode;
    toggleModal?(): void;
}

export interface IBusinessOrganizationListState {
    isReady: boolean;
    formItems: IUserFormItem[];
    isFormActive: boolean;
    formType: FormType;
    selectedUserData?: BusinessPartnerUser;

    displayedUserData?: any;
    refresh: boolean;
    isModalOpen: boolean;
    hasFormError: boolean;
    hasActionError: boolean;
    isStatementRequestModalOpen: boolean;
    statementRequestFromDateString: string;
    statementRequestToDateString: string;
    isStatementRequestAdminDropdownOpen: boolean;
    statementRequestFullOrg: boolean;
}

type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export enum FormType {
    None = 'NONE',
    Edit = 'EDIT',
    Remove = 'REMOVE',
    View = 'VIEW',
    Add = 'ADD'
}

export enum InputType {
    Text = 'text',
    Email = 'email'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    B2BUserId = 'B2BUserId',
    Email = 'Email',
    SpendingLimit = 'SpendingLimit',
    Status = 'Status',
    FullName = 'Name'
}

export interface IFormViewProps {
    modalClassName: string;
    FormWrapper: INodeProps;
    modalHeading?: string;
    modalDescription?: React.ReactNode;
    inputs?: IBusinessFormItem[];

    selectedUser?: any;
    buttons?: React.ReactNode[];
    errorMessage?: React.ReactNode[];
    resources: IBusinessOrganizationListResources;
    toggle?(): void;
}

export const renderInputSection = (inputs: IUserFormItem[], className: string, onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void): IBusinessFormItem[] => {
    return inputs.map((element) => {
        const { customClass, id, type, label, maxChars, isRequired, pattern, value, isDisabled } = element;

        return {
            wrapper: {
                className: classnames(className, id, customClass)
            },
            key: id,
            label: <FormLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <FormFieldError {...{ className, type: 'item' }} />,
            input: (
                <FormInput
                    {
                        ...{
                            id,
                            type,
                            maxLength: maxChars,
                            pattern,
                            value,
                            className,
                            isRequired: isRequired === undefined ? false : isRequired,
                            onChange: onInputChange,
                            isDisabled
                        }
                    }
                />
            )
        };
    });
};

interface IUserTableItemProps extends ITableItemProps {
    user: BusinessPartnerUser;
}

/**
 *
 * BusinessOrganizationList component.
 * @extends {React.PureComponent<IBusinessOrganizationListProps<IBusinessOrganizationListData>>}
 */
@observer
class BusinessOrganizationList extends React.Component<IBusinessOrganizationListProps<IBusinessOrganizationListData>, IBusinessOrganizationListState> {

    public BusinessFormDefaultInputs: IUserFormItem[] = [
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.businessUserFirstNameLabelText,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.businessUserLastNameLabelText,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.businessUserEmailAddressLabelText,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-100'
        },
        {
            id: InputID.SpendingLimit,
            type: InputType.Text,
            label: this.props.resources.businessUserSpendingLimitLabelText,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        }
    ];

    private readonly moduleClassName: string = 'ms-business-organization-list';

    private readonly mobileExcludedColumns: string[];

    @observable
    private _viewport: GridSize;

    @computed get isMobile(): boolean {
        return (this._viewport === 'xs' || this._viewport === 'sm');
    }

    @computed get canSubmitStatementRequest(): boolean {
        return (!StringExtensions.isNullOrWhitespace(this.state.statementRequestFromDateString) && !StringExtensions.isNullOrWhitespace(this.state.statementRequestToDateString));
    }

    @computed get selectedUserName(): string {
        const { businessUserSelectedUserDisplayName } = this.props.resources;
        return this.state.selectedUserData ? businessUserSelectedUserDisplayName
            .replace('{firstName}', (this.state.selectedUserData.FirstName || ''))
            .replace('{lastName}', (this.state.selectedUserData.LastName || '')) : '';
    }

    private readonly telemetryContent?: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    constructor(props: IBusinessOrganizationListProps<IBusinessOrganizationListData>) {
        super(props);
        this._toggleModal = this._toggleModal.bind(this);
        this._onInputChange = this._onInputChange.bind(this);
        this._handleRemoveUserButton = this._handleRemoveUserButton.bind(this);

        this.mobileExcludedColumns = [InputID.Email, InputID.Status, InputID.SpendingLimit];
        this._viewport = props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._updateViewport = this._updateViewport.bind(this);

        this.state = {
            isReady: false,
            formItems: this.BusinessFormDefaultInputs,
            isFormActive: false,
            formType: FormType.None,
            refresh: true,
            isModalOpen: false,
            hasFormError: false,
            hasActionError: false,
            isStatementRequestModalOpen: false,
            statementRequestFromDateString: '',
            statementRequestToDateString: '',
            isStatementRequestAdminDropdownOpen: false,
            statementRequestFullOrg: false
        };

        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
        this._updateViewport();
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public render(): JSX.Element | null {
        const { config, resources, data: { users } } = this.props;
        const { className, heading } = config;
        const { businessUserAddUserButtonText, businessUserLoadingMessage, businessUserEmptyListMessage, businessUserErrorGettingUsersMessage, userAccountStatementLabel, fullOrganizationLabel,
            requestStatementButtonLabel, statementRequestModalHeaderLabel, cancelLabel, fromDateLabel, toDateLabel, submitRequestLabel, sendToEmailLabel, selectedUserLabel } = resources;
        const { formItems, isFormActive, formType, isModalOpen, statementRequestFromDateString, statementRequestToDateString, isStatementRequestModalOpen } = this.state;

        const hasUsers = users && users.result && users.result.length > 0;
        this.payLoad.contentAction.etext = TelemetryConstant.AddUser;
        const addUserAttributes = getTelemetryAttributes(this.telemetryContent!, this.payLoad);

        const customer = this.props.data.customerInformation?.result;

        if (!hasUsers) {
            let errorMessage = '';
            if (users.status === 'LOADING') {
                errorMessage = businessUserLoadingMessage;
            } else if (users.status === 'FAILED') {
                errorMessage = businessUserErrorGettingUsersMessage;
            } else if (users.result && users.result.length === 0) {
                errorMessage = businessUserEmptyListMessage;
            }

            const errorProps = {
                ...this.props,
                className,
                OrganizationUserList: {
                    moduleProps: this.props,
                    className: classnames(this.moduleClassName, className)
                },
                moduleContainer: {
                    className: `${this.moduleClassName}__container`
                },
                heading: heading && <Heading className={`${this.moduleClassName}__heading`} headingTag={heading.tag} text={heading.text} />,
                addUserButton: <Button className={`${this.moduleClassName}__add-user-button`} onClick={this._handleAddUserButtonClicked} {...addUserAttributes} >
                    {businessUserAddUserButtonText}
                </Button>,
                noUsersText: <p className={`${this.moduleClassName}__error-message`}>
                    {errorMessage}
                </p>
            };

            return this.props.renderView(errorProps) as React.ReactElement;
        }

        const viewProps = {
            ...this.props,
            className,
            OrganizationUserList: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            moduleContainer: {
                className: `${this.moduleClassName}__container`
            },
            heading: heading && <Heading className={`${this.moduleClassName}__heading`} headingTag={heading.tag} text={heading.text} />,
            modalState: isModalOpen,
            addUserButton: <Button className={`${this.moduleClassName}__add-user-button`} onClick={this._handleAddUserButtonClicked} {...addUserAttributes} >
                {businessUserAddUserButtonText}
            </Button>,
            organizationListTable: <Table {...this._createTableProps()} />,
            toggleModal: this._toggleModal,
            formType,
            form: isFormActive && isModalOpen && (formType === FormType.Add || formType === FormType.Edit) ? this._renderUserForm() : this._renderUserDetails(),
            formItems,
            requestAccountStatementButton: (
                <AdminRequestStatementButton
                    className={classnames(this.moduleClassName, className)}
                    onRequestStatement={this._showRequestStatementModal}
                    requestStatementButtonLabel={requestStatementButtonLabel}
                    onToggleDropdown={this._toggleStatementRequestDropdown}
                    myUserAccountLabel={userAccountStatementLabel}
                    fullOrganizationLabel={fullOrganizationLabel}
                    isDropdownShowing={this.state.isStatementRequestAdminDropdownOpen}
                />
            ),
            requestStatementModal: (
                <RequestStatementModal
                    email={customer?.Email || ''}
                    isModalOpen={isStatementRequestModalOpen}
                    toggle={this._closeStatementRequestModal}
                    fromDateString={statementRequestFromDateString}
                    toDateString={statementRequestToDateString}
                    onChangeFromDate={this._updateFromDate}
                    onChangeToDate={this._updateToDate}
                    canSubmitRequest={this.canSubmitStatementRequest}
                    modalHeaderLabel={statementRequestModalHeaderLabel}
                    sendToEmailLabel={sendToEmailLabel}
                    fromDateLabel={fromDateLabel}
                    toDateLabel={toDateLabel}
                    submitRequestLabel={submitRequestLabel}
                    cancelLabel={cancelLabel}
                    onSubmitRequest={this._requestStatement}
                    className={classnames(this.moduleClassName, className)}
                    selectedUserLabel={selectedUserLabel}
                    selectedUser={this.state.statementRequestFullOrg ? '' : this.selectedUserName}
                />
            )
        } as IBusinessOrganizationListViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    // _createTableProps: creates the props for the organization list table
    private _createTableProps(): IDataTableProps {
        const { config, resources, context } = this.props;
        const { tableSort, showPagination, paginationItemPerPage } = config;
        const { businessUserPaginationNextButtonText, businessUserPaginationPreviousButtonText, businessUserEditButtonText, businessUserViewUserButtonText,
            businessUserDeleteButtonText, businessUserActionButtonText, businessUserPaginationAriaLabel, columnSortAriaLabel } = resources;

        const tableData = this._createTableItems();
        const tableHeading = tableData.length > 0 && tableData[0].row.map((item) => {
            if (this.isMobile && this.mobileExcludedColumns.includes(item.id)) {
                return undefined;
            }
            return {
                name: item.id,
                sortable: tableSort && tableSort || false,
                ariaLabel: columnSortAriaLabel
            } as IHeadingsProperty;

        }) || [];

        return {
            resources,
            headings: tableHeading,
            rows: tableData,
            className: `${this.moduleClassName}__table`,
            editLinkText: this.isMobile ? businessUserEditButtonText : '', // '' === showing just icon
            deleteLinkText: this.isMobile ? businessUserDeleteButtonText : '', // '' === showing just icon
            viewLinkText: businessUserViewUserButtonText,
            editLinkTooltip: businessUserEditButtonText,
            deleteLinkTooltip: businessUserDeleteButtonText,
            viewLinkTooltip: businessUserViewUserButtonText,
            enableToModify: true,
            actionLinkText: businessUserActionButtonText,
            showCheckBoxes: true,
            isSortable: tableSort,
            showPagination,
            minifyActions: this.isMobile,
            excludedColumns: this.isMobile ? this.mobileExcludedColumns : [],
            paginationProperty: {
                skipCount: context && context.request && context.request.query && context.request.query.skip && !isNaN(Number.parseInt(context.request.query.skip, 10)) ? Number.parseInt(context.request.query.skip, 10) : 0,
                itemPerPage: paginationItemPerPage || 10,
                prevText: businessUserPaginationPreviousButtonText,
                nextText: businessUserPaginationNextButtonText,
                paginationText: businessUserPaginationAriaLabel,
                url: context && this._getCurrentUrl(context.request).href || ''
            },
            isSingleSelectOnly: true,
            businessUserSelectCheckBoxAriaLabelText: resources.businessUserSelectCheckBoxAriaLabelText,
            actions: {
                onDelete: this._handleDeleteIcon,
                onEdit: this._handleEditIcon,
                onView: this.isMobile && this._handleViewDetails || undefined
            },
            formatPrice: this._formatPrice,
            checkBoxCallback: this._onSelectRow
        };
    }

    // _createTableItems: converts users recieved from the data-action(IBusinessParterUser[]) to tableRows(ITableItemProps[])
    private _createTableItems(): ITableRowProps[] {
        const { resources, data: { users } } = this.props;
        const { businessUserActiveStatusText, businessUserPendingStatusText, businessUserRemovedStatusText } = resources;
        const usersList = users && users.result;

        if (!usersList || usersList.length === 0) {
            return [];
        }

        return usersList.map((user) => {
            let statusString = '';

            switch (user.StatusValue) {
                case 0:
                    statusString = businessUserPendingStatusText;
                    break;
                case 1:
                    statusString = businessUserActiveStatusText;
                    break;
                case 2:
                default:
                    statusString = businessUserRemovedStatusText;
            }

            return {
                row: [
                    {
                        id: InputID.FullName,
                        type: TableDataType.Text,
                        value: `${user.FirstName} ${user.LastName}`,
                        user
                    } as IUserTableItemProps,
                    {
                        id: InputID.Email,
                        type: TableDataType.Text,
                        value: user.Email || '',
                        className: `${this.moduleClassName}__padRight`
                    } as IUserTableItemProps,
                    {
                        id: InputID.Status,
                        type: TableDataType.Text,
                        value: statusString
                    } as IUserTableItemProps,
                    {
                        id: InputID.SpendingLimit,
                        type: TableDataType.Price,
                        value: user.SpendingLimit || 0
                    } as IUserTableItemProps
                ],
                isSelected: false
            };
        });
    }

    // _toggleModal: closes the modal and resets the form states
    private _toggleModal(): void {
        this.setState({
            isModalOpen: false,
            isFormActive: false,
            formType: FormType.None
        });
    }

    // _handleAddUserButtonClicked: resets input fields, opens the modal, and sets the form states
    private readonly _handleAddUserButtonClicked = () => {
        // Reset form
        const newForm = this.state.formItems;
        newForm.forEach((input) => {
            if (input.id === InputID.Email) {
                // Make sure email input is re enabled (it gets disabled when admin is editing user)
                input.isDisabled = false;
            }

            input.value = '';
        });

        const newUser = { B2BUserId: Random.Guid.generateGuid() } as BusinessPartnerUser;

        this.setState({
            formItems: newForm,
            isFormActive: true,
            formType: FormType.Add,
            isModalOpen: true,
            hasFormError: false,
            hasActionError: false,
            selectedUserData: newUser
        });
    };

    // _handleAddEditFormSaveButton: triggered on the save button for both add/edit forms. Calls the appropriate data-action and reloads the page when successful
    private readonly _handleAddEditFormSaveButton = async () => {
        const { context } = this.props;
        const { formType, formItems } = this.state;

        const updatedUser = this._createBusinessPartner(formItems);

        if (formType === FormType.Edit) {
            editOrganizationUser(new EditOrganizationUserInput(updatedUser), context.actionContext)
                .then(() => {
                    window.location.reload(false);
                })
                .catch(error => {
                    context.telemetry.trace(error);
                    this.setState({
                        hasActionError: true
                    });
                });
        } else {
            addOrganizationUser(new AddOrganizationUserInput(updatedUser), context.actionContext)
                .then(() => {
                    window.location.reload(false);
                })
                .catch(error => {
                    context.telemetry.trace(error);
                    this.setState({
                        hasActionError: true
                    });
                });
        }
    };

    // _createBusinessPartner: attempts to convert input values(IUserFormItem) to a new user(BusinessPartnerUser)
    private readonly _createBusinessPartner = (inputs: IUserFormItem[]): BusinessPartnerUser => {
        const newUser: BusinessPartnerUser = { ...this.state.selectedUserData! };

        inputs.forEach((input) => {
            // Deals with input values need need to be converted into numbers
            if (input.id === InputID.SpendingLimit) {
                newUser[input.id] = Number.parseFloat(input.value) || 0;
            } else {
                newUser[input.id] = input.value;
            }
        });

        return newUser;
    };

    // _handleFormCancelButton: closes the modal when clicking the Cancel button
    private readonly _handleFormCancelButton = () => {
        this.setState({
            isFormActive: false,
            isModalOpen: false,
            formType: FormType.None
        });
    };

    // _handleEditIcon: converts the table row data(ITableItemProps[]) to input fields(IUserFormItem) and opens the edit form
    private readonly _handleEditIcon = (userData: ITableItemProps[]) => {
        // Split full name into first and last
        const possibleName = userData.find(cell => {
            return cell.id === 'Name';
        });
        const fullName = possibleName && possibleName.value as string || '';
        const lastSpaceIndex = fullName.lastIndexOf(' ');
        const firstName = fullName.substring(0, lastSpaceIndex);

        const lastName = fullName.substring(lastSpaceIndex + 1);

        // Create clone of formItems, map users information, update state
        const { formItems } = this.state;
        const newForm = formItems;

        newForm.forEach(input => {
            switch (input.id) {
                case InputID.FirstName:
                    input.value = firstName;
                    break;
                case InputID.LastName:
                    input.value = lastName;
                    break;
                case InputID.Email: // Email can never be null/undefined as it is a required field
                    input.isDisabled = true;
                    const possibleCell = userData.find(cell => {
                        return input.id === cell.id;
                    });
                    input.value = possibleCell && possibleCell.value as string || '';
                    break;
                default:
                    const dataCell = userData.find(cell => {
                        return input.id === cell.id;
                    });
                    input.value = dataCell && dataCell.value as string || '';
            }
        });

        this.setState({
            formItems: newForm,
            isFormActive: true,
            formType: FormType.Edit,
            isModalOpen: true,
            hasFormError: false,
            hasActionError: false,
            selectedUserData: (userData[0] as IUserTableItemProps).user
        });
    };

    // _handleDeleteIcon: opens removeUser confirmation window
    private readonly _handleDeleteIcon = (userData: ITableItemProps[]) => {
        const newUser: BusinessPartnerUser = { ...(userData[0] as IUserTableItemProps).user };

        const displayedUserData: any = {};

        userData.forEach((cell: ITableItemProps) => {
            if (cell.id === 'Name') {
                // Split full name into first and last
                const fullName = cell.value as string;
                const lastSpaceIndex = fullName.lastIndexOf(' ');
                displayedUserData.FirstName = fullName.substring(0, lastSpaceIndex);

                displayedUserData.LastName = fullName.substring(lastSpaceIndex + 1);
            } else if (cell.type === TableDataType.Price) {
                displayedUserData[cell.id] = this.props.context.cultureFormatter.formatCurrency(cell.value as string);
            } else {
                displayedUserData[cell.id] = cell.value;
            }
        });

        this.setState({
            displayedUserData,
            selectedUserData: newUser,
            formType: FormType.Remove,
            isModalOpen: true,
            isFormActive: false
        });
    };

    // _handleViewDetails: opens user details window
    private readonly _handleViewDetails = (userData: ITableItemProps[]) => {
        const newUser: BusinessPartnerUser = { ...(userData[0] as IUserTableItemProps).user };

        const displayedUserData: any = {};

        userData.forEach((cell: ITableItemProps) => {
            if (cell.id === 'Name') {
                // Split full name into first and last
                const fullName = cell.value as string;
                const lastSpaceIndex = fullName.lastIndexOf(' ');
                displayedUserData.FirstName = fullName.substring(0, lastSpaceIndex);

                displayedUserData.LastName = fullName.substring(lastSpaceIndex + 1);
            } else if (cell.type === TableDataType.Price) {
                displayedUserData[cell.id] = this.props.context.cultureFormatter.formatCurrency(cell.value as string);
            } else {
                displayedUserData[cell.id] = cell.value;
            }
        });

        this.setState({
            displayedUserData,
            selectedUserData: newUser,
            formType: FormType.View,
            isModalOpen: true,
            isFormActive: false
        });
    };

    // _handleRemoveUserButton: triggered on confirmation of removing a user.
    private readonly _handleRemoveUserButton = () => {
        // TODO
        const { context } = this.props;
        const { selectedUserData: deleteUserData } = this.state;

        if (deleteUserData) {
            deleteOrganizationUser(new DeleteOrganizationUserInput(deleteUserData), context.actionContext)
                .then(() => {
                    window.location.reload(false);
                })
                .catch((error) => {
                    context.telemetry.trace(error);
                });
        }
        this.setState({
            formType: FormType.None,
            isModalOpen: false
        });
    };

    private readonly callbackRemoveButton = () => {
        this._handleRemoveUserButton();
    };

    // _renderUserDetails: creates the remove/view form props
    private _renderUserDetails(): IFormViewProps {
        const { resources } = this.props;
        const { formType, displayedUserData } = this.state;
        const { businessUserRemoveModalHeaderText, businessUserRemoveModalDescription, businessUserViewModalHeaderText,
            businessUserCancelButtonText, businessUserCancelRemoveUserButtonText, businessUserRemoveUserButtonText, businessUserErrorUpdatingUsersMessage } = resources;

        const formClassName = `${this.moduleClassName}__form`;
        const typeClassName = formType === FormType.Remove ? 'type-remove' : 'type-view';
        const handleRemoveButton = this.callbackRemoveButton;

        return {
            modalClassName: classnames(`${this.moduleClassName}__modal`, typeClassName),
            FormWrapper: {
                className: formClassName
            },
            modalHeading: formType === FormType.Remove ? businessUserRemoveModalHeaderText : businessUserViewModalHeaderText,
            modalDescription: formType === FormType.Remove && <p className={`${formClassName}-description`}>
                {businessUserRemoveModalDescription}
            </p>,
            selectedUser: displayedUserData,
            buttons: [
                formType === FormType.Remove && <Button key='action-remove' className={`${formClassName}-remove-user-button`} onClick={handleRemoveButton}>
                    {businessUserRemoveUserButtonText}
                </Button>,
                <Button key='action-cancel' className={`${formClassName}-cancel`} onClick={this._handleFormCancelButton}>
                    {formType === FormType.Remove ? businessUserCancelRemoveUserButtonText : businessUserCancelButtonText}
                </Button>
            ],
            errorMessage: [
                <FormFieldError
                    key='fieldIncorrect' id='fieldIncorrect' className={`${formClassName}-error`}
                    message={businessUserErrorUpdatingUsersMessage} />
            ],
            resources
        };
    }

    // _renderUserForm: creates the add/edit form props
    private _renderUserForm(): IFormViewProps {
        const { resources } = this.props;
        const { formType, formItems, hasFormError, hasActionError, isReady } = this.state;
        const { businessUserEditUserFormHeadingText, businessUserAddUserFormHeadingText, businessUserSaveButtonArialabel,
            businessUserSaveButtonText, businessUserCancelButtonArialabel, businessUserCancelButtonText, businessUserFieldIncorrectErrorText, businessUserActionErrorText, businessUserAllFieldsRequiredMessage } = resources;

        const formClassName = `${this.moduleClassName}__form`;
        const typeClassName = formType === FormType.Add ? 'type-add' : 'type-edit';

        return {
            modalClassName: classnames(`${this.moduleClassName}__modal`, typeClassName),
            FormWrapper: {
                className: formClassName
            },
            modalHeading: formType === FormType.Add ? businessUserAddUserFormHeadingText : businessUserEditUserFormHeadingText,
            modalDescription: formType === FormType.Add && <p className={`${formClassName}-all-required-msg`}>
                {businessUserAllFieldsRequiredMessage}
            </p>,
            inputs: renderInputSection(formItems, `${formClassName}-item`, this._onInputChange),
            buttons: [
                (
                    <FormButton
                        key='continue'
                        id='continue'
                        className={`${formClassName}-save`}
                        ariaLabel={businessUserSaveButtonArialabel}
                        text={businessUserSaveButtonText}
                        onClick={this._handleAddEditFormSaveButton}
                        disabled={!isReady}
                        telemetryContent={this.telemetryContent}
                    />),
                (
                    <FormButton
                        key='cancel'
                        id='cancel'
                        className={`${formClassName}-cancel`}
                        ariaLabel={businessUserCancelButtonArialabel}
                        text={businessUserCancelButtonText}
                        onClick={this._handleFormCancelButton}
                        telemetryContent={this.telemetryContent}
                    />)
            ],
            errorMessage: [
                hasFormError && <FormFieldError
                    key='fieldIncorrect' id='fieldIncorrect' className={this.moduleClassName}
                    message={businessUserFieldIncorrectErrorText} />,
                hasActionError && <FormFieldError
                    key='fieldIncorrect' id='fieldIncorrect' className={this.moduleClassName}
                    message={businessUserActionErrorText} />
            ],
            resources
        };
    }

    // _onInputChange: updates the fomrItems state on every input change
    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const newForm = this.state.formItems;

        newForm.forEach(item => {
            if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });

        // Update state object with latest values and check if form is ready
        // TODO: remove !refresh
        this.setState((prev) => ({
            refresh: !prev.refresh,
            isReady: this._isFormReady(),
            formItems: newForm,
            hasFormError: false,
            hasActionError: false
        }));
    };

    // _isFormReady: checks whether the current values in the input fields are valid for submission, this will also disable/enable the submit button.
    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;

        for (const formItem of formItems) {
            // Will return TRUE if:
            //  - input field is required and EMPTY
            //  OR
            //  - pattern provided and value does not match pattern
            //  OR
            //  - if spendingLimit input --> make sure value can be parsed into an NUMBER
            if ((formItem.isRequired && formItem.value.length === 0)) {
                return false;
            } else if (formItem.pattern !== undefined && formItem.value.match(formItem.pattern as string) === null) {
                return false;
            } else if (formItem.id === InputID.SpendingLimit && formItem.value !== '' && isNaN(Number.parseFloat(formItem.value))) {
                this.setState({
                    hasFormError: true
                });
                return false;
            }
        }

        return true;
    };

    private readonly _formatPrice = (price: number): string => {
        return this.props.context.cultureFormatter ? this.props.context.cultureFormatter.formatCurrency(price) : price.toString();
    };

    private readonly _getCurrentUrl = (reqContext: IRequestContext): URL => {
        if (MsDyn365.isBrowser) {
            return new URL(window.location.href);
        }

        // NOTE: Typing on requestURL is incorrect
        if (reqContext.url.requestUrl.href) {
            return new URL(reqContext.url.requestUrl.href);
        }
        return new URL(reqContext.url.requestUrl.href);

    };

    private _updateViewport(): void {
        this._viewport = this._getViewport();
    }

    private readonly _getViewport = (): GridSize => {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (typeof window !== undefined && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';

            }
        }

        return 'xs';
    };

    private readonly _toggleStatementRequestDropdown = (): void => {
        this.setState({
            isStatementRequestAdminDropdownOpen: !this.state.isStatementRequestAdminDropdownOpen
        });
    };

    private readonly _updateFromDate = (newFromDate: string) => {
        this.setState({
            statementRequestFromDateString: newFromDate
        });
    };

    private readonly _updateToDate = (newToDate: string) => {
        this.setState({
            statementRequestToDateString: newToDate
        });
    };

    private readonly _showRequestStatementModal = (isFullOrg: boolean) => {
        this.setState({
            statementRequestFullOrg: isFullOrg,
            isStatementRequestModalOpen: true
        });
    };

    private readonly _requestStatement = () => {
        const customer = this.props.data.customerInformation.result!;
        const businessPartnerUsers = this.props.data.users.result!;

        const currentUser = businessPartnerUsers.find(element => element.Email === customer.Email);
        const B2BUserId = this._getB2BUserId(currentUser);

        const statementRequest: AccountStatementRequest = {
            FromDate: new Date(this.state.statementRequestFromDateString),
            ToDate: new Date(this.state.statementRequestToDateString),
            IsForFullOrganization: this.state.statementRequestFullOrg,
            RequestedUserId: B2BUserId,
            RequestingUserEmail: customer.Email,
            DeliveryTypeValue: BusinessPartnerOperationDeliveryType?.Email || 1
        };

        const context = { callerContext: this.props.context.actionContext };

        BusinessPartnerUsersDataActions.requestAccountStatementAsync(context, B2BUserId, statementRequest);

        this._closeStatementRequestModal();
    };

    private readonly _getB2BUserId = (currentUser: BusinessPartnerUser | undefined) => {
        if (this.state.statementRequestFullOrg || !this.state.selectedUserData) {
            return currentUser ? currentUser.B2BUserId : '';
        }
        return this.state.selectedUserData.B2BUserId;

    };

    private readonly _closeStatementRequestModal = () => {
        this.setState({
            isStatementRequestModalOpen: false
        });
    };

    private readonly _onSelectRow = (records: ITableRowProps[]) => {
        const selectedRow = records.find(rowProps => rowProps.isSelected);

        this.setState({
            selectedUserData: selectedRow ? (selectedRow.row[0] as IUserTableItemProps).user : undefined
        });
    };
}

export default BusinessOrganizationList;
